import { memo } from "react"
import { useQuery } from "@rocicorp/zero/react"

import type { AgentRecommendation } from "@productlane/zero/schema"
import { isEngineeringRecommendation } from "@productlane/lib/src/agent-recommendations"
import { Button } from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import { useShortcut } from "@/lib"
import { useRole } from "@/lib/useRole"
import { AgentMessage } from "./agent-message-header"
import { ArticleMention } from "./article-mention"
import { CapabilityGuide } from "./capability-guide"
import { DecisionBar } from "./decision-bar"
import { ButtonKeys, DeclineButton, isTyping } from "./decline-button"
import { useDocsSaves, useProposalGroupRows } from "./docs-change"
import { DocsChangeText } from "./docs-change-text"
import { CreateIssueButton, LinkedIssueBar } from "./engineering-issue-panel"
import { closedIdeaLabel, ideaOrigin, ideaPlanParts } from "./format"
import { GuidanceChange } from "./guidance-change"
import { IdeaExamples } from "./idea-examples"
import { IDEA_EXAMPLES_COPY, isExampleLoading } from "./idea-examples-model"
import { SeverityIcon } from "./recommendation-severity"
import { useEngineeringIssue } from "./use-engineering-issue"
import { useExampleAnswersBackfill } from "./use-example-answers"
import {
	DISMISS_REASONS,
	DOCS_DECLINE_REASONS,
	isDocsType,
	useRecommendationActions,
} from "./use-recommendation-actions"

const CARD =
	"flex flex-col gap-5 rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700/80 dark:bg-gray-800"
const PROSE = "flex flex-col gap-3 text-sm leading-6 text-primary"

// The agent's first message in an idea thread: the conversation it noticed, what it
// would change and why, then the change itself with the decision.
export const IdeaMessage = memo(
	({
		recommendation,
		onClosed,
	}: {
		recommendation: AgentRecommendation
		onClosed: () => void
	}) => {
		const { role } = useRole()
		const canAct = role !== "VIEWER"
		// Live, so a teammate's accept or decline shows here at once.
		const [live] = useQuery(
			queries.agentRecommendation({ id: recommendation.id }),
		)
		const [members] = useQuery(queries.teamMembers())
		const status = live?.status ?? recommendation.status
		const closedBy = members.find(
			(member) => member.user?.id === live?.closedByUserId,
		)?.user?.name
		const { type, payload } = recommendation
		const isDocs = isDocsType(type)
		const docs = useProposalGroupRows(
			isDocs ? (recommendation.proposalGroupId ?? null) : null,
		)
		const docsSaves = useDocsSaves()
		const { accept, dismiss, busy } = useRecommendationActions({
			recommendation,
			docsGroup: docs.group,
			docsRows: docs.rows,
			docsSaves,
			onClosed,
		})
		const docsMissing = isDocs && docs.loaded && !docs.rows[0]
		const engineering = isEngineeringRecommendation(type)
		const filing = useEngineeringIssue(recommendation)
		const filedIssueId = filing.issue?.issueId ?? ""
		const [filedRow] = useQuery(queries.issueById(filedIssueId), {
			enabled: !!filedIssueId,
		})
		const open = status === "OPEN" && !filing.issue
		// Live, so answers the server backfills show without a reload.
		const livePayload = live?.payload ?? payload
		const evidence = livePayload.evidence ?? []
		const backfill = useExampleAnswersBackfill(recommendation.id, livePayload)
		const loadingThreadIds = evidence
			.filter((e) =>
				isExampleLoading({
					payload: livePayload,
					threadId: e.threadId,
					...backfill,
				}),
			)
			.map((e) => e.threadId)
		// Always the same shape, so every idea opens on where it came from.
		const origin = ideaOrigin({
			conversationCount: recommendation.conversationCount,
			customerCount: payload.askers?.length || recommendation.conversationCount,
			since: payload.since,
			learnedFrom: payload.learnedFrom,
		})
		const plan = ideaPlanParts(type, docs.rows)
		const planRow = docs.rows[0]
		const planIcon =
			planRow?.proposals.find((p) => p.proposedIcon)?.proposedIcon ??
			(planRow?.articleId
				? docs.articles.get(planRow.articleId)?.icon
				: undefined) ??
			"file"
		const canAccept = canAct && open && !engineering && !busy && !docsMissing
		useShortcut(
			"1",
			(event) => {
				event.preventDefault()
				void accept()
			},
			{ enabled: canAccept, ignoreEventWhen: isTyping },
		)

		return (
			<div data-testid="agent-idea-message" className="px-1 pb-6">
				<AgentMessage at={recommendation.createdAt}>
					<div className="flex min-w-0 flex-col gap-4 pt-1">
						<div className={PROSE}>
							<p data-testid="agent-idea-origin">
								{/* Inline, so the sentence keeps the left edge of the others. */}
								<SeverityIcon
									className="mr-1.5 inline-flex align-[-2px]"
									impactPerMonth={recommendation.impactPerMonth}
								/>
								{origin}
							</p>
							<p data-testid="agent-idea-summary">{recommendation.summary}</p>
						</div>
						<div className={PROSE}>
							<p data-testid="agent-idea-plan">
								{plan.lead}
								{plan.article && (
									<>
										{" "}
										<ArticleMention
											title={plan.article.title}
											articleId={plan.article.articleId}
											icon={planIcon}
										/>
									</>
								)}
								{plan.tail}
							</p>
							{/* One card is enough: a docs change reads as its text, with the diff behind Preview. */}
							{isDocs && (
								<DocsChangeText
									rows={docs.rows}
									articles={docs.articles}
									loaded={docs.loaded}
								/>
							)}
						</div>
						<div className={isDocs ? "contents" : CARD}>
							{!isDocs && (
								<span className="text-xs font-medium text-tertiary">
									{IDEA_EXAMPLES_COPY.change}
								</span>
							)}
							{type === "GUIDANCE" && payload.guidance && (
								<GuidanceChange
									recommendationId={recommendation.id}
									change={payload.guidance}
									editing={false}
								/>
							)}

							{engineering && payload.guide && (
								<CapabilityGuide
									recommendationId={recommendation.id}
									guide={payload.guide}
								/>
							)}

							{filing.issue ? (
								<DecisionBar>
									<LinkedIssueBar
										issue={filing.issue}
										statusName={filedRow?.statusName}
									/>
								</DecisionBar>
							) : status !== "OPEN" ? (
								<DecisionBar>
									<p
										data-testid="agent-idea-closed"
										className="text-sm text-tertiary"
									>
										{closedIdeaLabel(status, closedBy)}
									</p>
								</DecisionBar>
							) : (
								canAct && (
									<DecisionBar>
										<DeclineButton
											label="Decline"
											reasons={isDocs ? DOCS_DECLINE_REASONS : DISMISS_REASONS}
											disabled={busy}
											shortcut
											onDecline={(reason, note) => void dismiss(reason, note)}
										/>
										{engineering ? (
											<CreateIssueButton
												defaultTitle={recommendation.title}
												disabled={busy}
												busy={filing.busy}
												onCreate={filing.create}
											/>
										) : (
											<Button
												isLoading={busy}
												disabled={busy || docsMissing}
												onClick={() => void accept()}
											>
												Accept
												<ButtonKeys>1</ButtonKeys>
											</Button>
										)}
									</DecisionBar>
								)
							)}
						</div>
						{evidence.length > 0 && (
							<div className={CARD}>
								<IdeaExamples
									key={recommendation.id}
									evidence={evidence}
									loadingThreadIds={loadingThreadIds}
									engineering={engineering}
									exampleAction={payload.guide?.exampleAction}
								/>
							</div>
						)}
					</div>
				</AgentMessage>
			</div>
		)
	},
)
